import type { ReactNode } from 'react';

interface ToggleBaseProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    id?: string;
    disabled?: boolean;
    /** aria-describedby: id of the element holding a hint for this switch. */
    ariaDescribedBy?: string;
}

interface VisibleLabel {
    /** Visible text that names the switch. */
    label: Exclude<ReactNode, null | undefined | boolean>;
    ariaLabel?: never;
}

interface HiddenLabel {
    label?: never;
    /** Name for a switch with no visible label beside it. */
    ariaLabel: string;
}

export type ToggleProps = ToggleBaseProps & (VisibleLabel | HiddenLabel);

const LABEL_CLASSES = 'inline-flex items-center gap-3 text-sm';
const LABEL_ENABLED_CLASSES = 'cursor-pointer text-mp-foreground';
const LABEL_DISABLED_CLASSES = 'cursor-not-allowed text-mp-muted';
const SWITCH_CLASSES = 'relative inline-flex h-6 w-11 shrink-0';
const SWITCH_DISABLED_CLASSES = 'opacity-50';
const TRACK_CLASSES =
    'absolute inset-0 rounded-full bg-mp-muted transition-colors motion-reduce:transition-none peer-checked:bg-mp-primary peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-mp-link';
const THUMB_CLASSES =
    'pointer-events-none absolute left-0.5 top-0.5 size-5 rounded-full bg-mp-surface transition-transform motion-reduce:transition-none peer-checked:translate-x-5';

export function Toggle({
    checked,
    onChange,
    id,
    disabled = false,
    ariaDescribedBy,
    label,
    ariaLabel,
}: ToggleProps) {
    return (
        <label
            htmlFor={id}
            className={`${LABEL_CLASSES} ${disabled ? LABEL_DISABLED_CLASSES : LABEL_ENABLED_CLASSES}`}
        >
            <span className={`${SWITCH_CLASSES} ${disabled ? SWITCH_DISABLED_CLASSES : ''}`}>
                <input
                    id={id}
                    type="checkbox"
                    role="switch"
                    className="peer sr-only"
                    checked={checked}
                    disabled={disabled}
                    onChange={(event) => onChange(event.target.checked)}
                    aria-label={ariaLabel}
                    aria-describedby={ariaDescribedBy}
                />
                <span aria-hidden="true" className={TRACK_CLASSES} />
                <span aria-hidden="true" className={THUMB_CLASSES} />
            </span>
            {label !== undefined && <span>{label}</span>}
        </label>
    );
}
